/* eslint-disable max-lines */
import { useString, useStrings } from "../../localization/localizationHelpers";
import {
  aaaGameInformationValidationSchemas,
  idXboxGameInformationValidationSchemas,
  idXboxIntroValidationSchemas,
  pitchInformationValidationSchemas,
} from "./formValidationSchemas";

/** ************************************* ID@Xbox Introduction Validation Hooks **************************************************/

export const useIdXboxIntroContactValidationSchema = () => {
  const [
    required,
    invalidPhoneNumberFormat,
    invalidEmailFormat,
    maxLengthRequiredErrorMessage,
    pleaseAddValidUrlValidation,
  ] = useStrings(
    "required",
    "invalidPhoneNumberFormat",
    "invalidEmailFormat",
    "maxLengthRequiredErrorMessage",
    "pleaseAddValidUrlValidation"
  );
  return idXboxIntroValidationSchemas({
    required,
    invalidPhoneNumberFormat,
    invalidEmailFormat,
    maxLengthRequiredErrorMessage,
    pleaseAddValidUrlValidation,
  }).contact;
};

export const useIdXboxIntroStudioValidationSchema = () => {
  const [
    pleaseAddValidUrlValidation,
    pleaseAddOneLinkValidation,
    required,
    maxLengthRequiredErrorMessage,
  ] = useStrings(
    "pleaseAddValidUrlValidation",
    "pleaseAddOneLinkValidation",
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return idXboxIntroValidationSchemas({
    pleaseAddValidUrlValidation,
    pleaseAddOneLinkValidation,
    required,
    maxLengthRequiredErrorMessage,
  }).studio;
};

/** *********************************** ID@Xbox Game Information Validation Hooks ************************************************/

export const useIdXboxGameInformationConceptValidationSchema = () => {
  const [
    pleaseAddOneDeveloperValidation,
    pleaseAddValidUrlValidation,
    pleaseAddOneLinkValidation,
    required,
    maxLengthRequiredErrorMessage,
  ] = useStrings(
    "pleaseAddOneDeveloperValidation",
    "pleaseAddValidUrlValidation",
    "pleaseAddOneLinkValidation",
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return idXboxGameInformationValidationSchemas({
    pleaseAddOneDeveloperValidation,
    pleaseAddValidUrlValidation,
    pleaseAddOneLinkValidation,
    required,
    maxLengthRequiredErrorMessage,
  }).details;
};

export const useIdXboxGameInformationGameExpectationsValidationSchema = () => {
  const [
    targetSystemsAndPlatforms,
    required,
    targetSystemsAndPlatformsRequiredFieldsSubtitle,
    maxLengthRequiredErrorMessage,
  ] = useStrings(
    "targetSystemsAndPlatforms",
    "required",
    "targetSystemsAndPlatformsRequiredFieldsSubtitle",
    "maxLengthRequiredErrorMessage"
  );
  return idXboxGameInformationValidationSchemas({
    targetSystemsAndPlatforms,
    required,
    targetSystemsAndPlatformsRequiredFieldsSubtitle,
    maxLengthRequiredErrorMessage,
  }).gameExpectations;
};

export const useIdXboxGameInformationPricingAndAvailabilityValidationSchema = () => {
  const [
    atLeastOneLanguage,
    selectAtLeastOneOption,
    selectAtLeastOneLanguage,
    required,
    suggestedRetailPriceErrorMessage,
    numberTypeError,
    maxLengthRequiredErrorMessage,
  ] = useStrings(
    "atLeastOneLanguage",
    "selectAtLeastOneOption",
    "selectAtLeastOneLanguage",
    "required",
    "suggestedRetailPriceErrorMessage",
    "numberTypeError",
    "maxLengthRequiredErrorMessage"
  );
  return idXboxGameInformationValidationSchemas({
    atLeastOneLanguage,
    selectAtLeastOneOption,
    selectAtLeastOneLanguage,
    required,
    suggestedRetailPriceErrorMessage,
    numberTypeError,
    maxLengthRequiredErrorMessage,
  }).pricingAndAvailability;
};

export const useIdXboxGameInformationTechnicalCapabilitiesValidationSchema = () => {
  const required = useString("required");
  return idXboxGameInformationValidationSchemas({
    required,
  }).technicalCapabilities;
};

export const useIdXboxGameInformationConfigurationsValidationSchema = () => {
  const [required, maxLengthRequiredErrorMessage] = useString(
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return idXboxGameInformationValidationSchemas({
    required,
    maxLengthRequiredErrorMessage,
  }).configurations;
};

export const useIdXboxGameInformationXboxNetworkValidationSchema = () => {
  const [required, maxLengthRequiredErrorMessage] = useString(
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return idXboxGameInformationValidationSchemas({ required, maxLengthRequiredErrorMessage })
    .xboxNetwork;
};

export const useIdXboxGameInformationUgcValidationSchema = () => {
  const [required, maxLengthRequiredErrorMessage] = useString(
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return idXboxGameInformationValidationSchemas({ required, maxLengthRequiredErrorMessage }).ugc;
};

export const useIdXboxGameInformationSupplementaryDataValidationSchema = () => {
  const [required, maxLengthRequiredErrorMessage] = useString(
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return idXboxGameInformationValidationSchemas({ required, maxLengthRequiredErrorMessage })
    .supplementaryData;
};

/** ************************************* Pitch information Validation Hooks **************************************************/

export const usePitchInformationPublisherDeveloperValidationSchema = () => {
  const [required, maxLengthRequiredErrorMessage] = useStrings(
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return pitchInformationValidationSchemas({
    required,
    maxLengthRequiredErrorMessage,
  }).publisherDeveloper;
};

export const usePitchInformationAboutYourGameValidationSchema = () => {
  const [required, maxLengthRequiredErrorMessage] = useStrings(
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return pitchInformationValidationSchemas({
    required,
    maxLengthRequiredErrorMessage,
  }).aboutYourGame;
};

export const usePitchInformationAskForXboxValidationSchema = () => {
  const [required, maxLengthRequiredErrorMessage] = useStrings(
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return pitchInformationValidationSchemas({
    required,
    maxLengthRequiredErrorMessage,
  }).askForXbox;
};

export const usePitchInformationAboutYourselfValidationSchema = () => {
  const [required, maxLengthRequiredErrorMessage] = useStrings(
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return pitchInformationValidationSchemas({
    required,
    maxLengthRequiredErrorMessage,
  }).aboutYourself;
};

/** *********************************** AAA Game Information Validation Hooks ************************************************/

export const useAaaGameInformationConceptValidationSchema = () => {
  const [
    pleaseAddOneDeveloperValidation,
    pleaseAddValidUrlValidation,
    pleaseAddOneLinkValidation,
    required,
    maxLengthRequiredErrorMessage,
  ] = useStrings(
    "pleaseAddOneDeveloperValidation",
    "pleaseAddValidUrlValidation",
    "pleaseAddOneLinkValidation",
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return aaaGameInformationValidationSchemas({
    pleaseAddOneDeveloperValidation,
    pleaseAddValidUrlValidation,
    pleaseAddOneLinkValidation,
    required,
    maxLengthRequiredErrorMessage,
  }).details;
};

export const useAaaGameInformationDistributionInformationValidationSchema = () => {
  const [required, maxLengthRequiredErrorMessage] = useStrings(
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return aaaGameInformationValidationSchemas({ required, maxLengthRequiredErrorMessage })
    .distributionInformation;
};

export const useAaaGameInformationGameExpectationsValidationSchema = () => {
  const [
    targetSystemsAndPlatforms,
    required,
    targetSystemsAndPlatformsRequiredFieldsSubtitle,
    maxLengthRequiredErrorMessage,
  ] = useStrings(
    "targetSystemsAndPlatforms",
    "required",
    "targetSystemsAndPlatformsRequiredFieldsSubtitle",
    "maxLengthRequiredErrorMessage"
  );
  return aaaGameInformationValidationSchemas({
    targetSystemsAndPlatforms,
    required,
    targetSystemsAndPlatformsRequiredFieldsSubtitle,
    maxLengthRequiredErrorMessage,
  }).gameExpectations;
};

export const useAaaGameInformationAvailabilityValidationSchema = () => {
  const [
    atLeastOneLanguage,
    selectAtLeastOneOption,
    selectAtLeastOneLanguage,
    required,
    suggestedRetailPriceErrorMessage,
    numberTypeError,
    maxLengthRequiredErrorMessage,
  ] = useStrings(
    "atLeastOneLanguage",
    "selectAtLeastOneOption",
    "selectAtLeastOneLanguage",
    "required",
    "suggestedRetailPriceErrorMessage",
    "numberTypeError",
    "maxLengthRequiredErrorMessage"
  );
  return aaaGameInformationValidationSchemas({
    atLeastOneLanguage,
    selectAtLeastOneOption,
    selectAtLeastOneLanguage,
    required,
    suggestedRetailPriceErrorMessage,
    numberTypeError,
    maxLengthRequiredErrorMessage,
  }).availability;
};

export const useAaaGameInformationTechnicalCapabilitiesValidationSchema = () => {
  const [required, maxLengthRequiredErrorMessage] = useString(
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return aaaGameInformationValidationSchemas({
    required,
    maxLengthRequiredErrorMessage,
  }).technicalCapabilities;
};

export const useAaaGameInformationConfigurationsValidationSchema = () => {
  const [required, maxLengthRequiredErrorMessage] = useString(
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return aaaGameInformationValidationSchemas({
    required,
    maxLengthRequiredErrorMessage,
  }).configurations;
};

export const useAaaGameInformationXboxNetworkValidationSchema = () => {
  const [required, maxLengthRequiredErrorMessage] = useString(
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return aaaGameInformationValidationSchemas({ required, maxLengthRequiredErrorMessage })
    .xboxNetwork;
};

export const useAaaGameInformationUgcSchema = () => {
  const [required, maxLengthRequiredErrorMessage] = useString(
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return aaaGameInformationValidationSchemas({ required, maxLengthRequiredErrorMessage }).ugc;
};

export const useAaaGameInformationSupplementaryDataSchema = () => {
  const [required, maxLengthRequiredErrorMessage] = useString(
    "required",
    "maxLengthRequiredErrorMessage"
  );
  return aaaGameInformationValidationSchemas({ required, maxLengthRequiredErrorMessage })
    .supplementaryData;
};
